import { fitFontSize, fitTextBlock, type MeasureFn, type Region, type TextBlock } from "./layout";

/**
 * Pure billboard layouts (M5, ADR-0009 #9): the "Advertise here" placeholder of an unrented board,
 * a text creative (title, monogram tile, brand and accent colours) and the contain-fit rectangle of
 * an image creative. All boards are 10 x 4 m (world v3), so one 1024 x 410 canvas texture fits them
 * exactly (no stretching). Text is measured through an injected function so vitest runs it without
 * a canvas; the painters in signPainter.ts only draw what these functions place.
 */
export const BILLBOARD_TEXTURE = { width: 1024, height: 410 } as const;

/** Wording of the placeholder; the price line only exists when the server's rate is known. */
export interface PlaceholderCopy {
  headline: string;
  /** e.g. "from $14/week" (BillboardView.dayRateCents x 7), or null before the board list arrives. */
  price: string | null;
  /** e.g. "Rent this board for 7 or 30 days". */
  detail: string;
}

export const PLACEHOLDER_HEADLINE = "Advertise here";

/** Whole US dollars without cents ("$14"), otherwise two decimals ("$14.50"); thousands separated. */
export function formatUsdCents(cents: number): string {
  const whole = Math.floor(cents / 100);
  const rest = cents % 100;
  const dollars = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return rest === 0 ? `$${dollars}` : `$${dollars}.${String(rest).padStart(2, "0")}`;
}

/** "from $14/week" from the server's day rate, or null when the rate is missing or implausible. */
export function weeklyPriceLabel(dayRateCents: unknown): string | null {
  if (typeof dayRateCents !== "number" || !Number.isSafeInteger(dayRateCents) || dayRateCents <= 0 || dayRateCents > 100_000_000) return null;
  return `from ${formatUsdCents(dayRateCents * 7)}/week`;
}

/** "Rent this board for 7 or 30 days" from the allowed terms (sorted, de-duplicated, plausible only). */
export function termsDetail(allowedTermDays: unknown): string {
  const days = Array.isArray(allowedTermDays)
    ? [...new Set(allowedTermDays.filter((d): d is number => typeof d === "number" && Number.isSafeInteger(d) && d > 0 && d <= 366))].sort((a, b) => a - b)
    : [];
  if (days.length === 0) return "Rent this billboard";
  if (days.length === 1) return `Rent this board for ${days[0]} ${days[0] === 1 ? "day" : "days"}`;
  const list = days.slice(0, 4);
  return `Rent this board for ${list.slice(0, -1).join(", ")} or ${list[list.length - 1]} days`;
}

export function placeholderCopy(view: { dayRateCents: unknown; allowedTermDays: unknown } | null): PlaceholderCopy {
  if (!view) return { headline: PLACEHOLDER_HEADLINE, price: null, detail: "Rent this billboard" };
  return { headline: PLACEHOLDER_HEADLINE, price: weeklyPriceLabel(view.dayRateCents), detail: termsDetail(view.allowedTermDays) };
}

export interface PlaceholderLayout {
  /** Inset decorative border. */
  frame: Region;
  headlineBox: Region;
  headline: TextBlock;
  /** Amber price pill (null without a price). */
  pill: { box: Region; fontPx: number; text: string } | null;
  detailBox: Region;
  detailPx: number;
  /** Bottom strip for the abstract skyline art (drawn under the text at low contrast). */
  art: Region;
}

/** Minimum text sizes on the 410 px tall texture (a 4 m board): readable from across the street. */
export const PLACEHOLDER_MIN_PX = { headline: 72, price: 40, detail: 24 } as const;

export function layoutPlaceholder(copy: PlaceholderCopy, bold: MeasureFn, semi: MeasureFn, W: number = BILLBOARD_TEXTURE.width, H: number = BILLBOARD_TEXTURE.height): PlaceholderLayout {
  const inset = Math.round(H * 0.035);
  const frame: Region = { x: inset, y: inset, w: W - inset * 2, h: H - inset * 2 };
  const sideX = Math.round(W * 0.07);
  const innerW = W - sideX * 2;
  const hasPrice = copy.price !== null;
  // Vertical rhythm (fractions of H): headline, price pill, detail line.
  const headlineBox: Region = hasPrice ? { x: sideX, y: H * 0.1, w: innerW, h: H * 0.34 } : { x: sideX, y: H * 0.16, w: innerW, h: H * 0.4 };
  const headline = fitTextBlock(bold, copy.headline, headlineBox, { maxPx: headlineBox.h * 0.86, minPx: PLACEHOLDER_MIN_PX.headline, maxLines: 1, lineHeightFactor: 1.08 });
  let pill: PlaceholderLayout["pill"] = null;
  let detailTop = headlineBox.y + headlineBox.h + H * 0.05;
  if (copy.price !== null) {
    const pillH = Math.round(H * 0.19);
    const fontPx = fitFontSize(bold, copy.price, innerW * 0.8, pillH * 0.66, pillH * 0.66, PLACEHOLDER_MIN_PX.price);
    const padX = Math.round(pillH * 0.45);
    const pillW = Math.min(innerW, Math.round(bold(copy.price, fontPx) + padX * 2));
    const y = Math.round(headlineBox.y + headlineBox.h + H * 0.03);
    pill = { box: { x: Math.round((W - pillW) / 2), y, w: pillW, h: pillH }, fontPx, text: copy.price };
    detailTop = y + pillH + H * 0.04;
  }
  const detailBox: Region = { x: sideX, y: detailTop, w: innerW, h: Math.min(H * 0.12, frame.y + frame.h - H * 0.04 - detailTop) };
  const detailPx = fitFontSize(semi, copy.detail, detailBox.w, detailBox.h * 0.9, detailBox.h * 0.9, PLACEHOLDER_MIN_PX.detail);
  const artH = Math.round(H * 0.42);
  const art: Region = { x: frame.x, y: frame.y + frame.h - artH, w: frame.w, h: artH };
  return { frame, headlineBox, headline, pill, detailBox, detailPx, art };
}

export interface CreativeLayout {
  pad: number;
  /** White (or padding-coloured) monogram tile. */
  tile: Region;
  monogramPx: number;
  titleBox: Region;
  title: TextBlock;
  /** Accent-colour band along the bottom edge. */
  band: Region;
}

export const CREATIVE_MIN_TITLE_PX = 30;

/** Text creative: monogram tile on the left, the title (<= 2 lines) to its right, an accent band below. */
export function layoutTextCreative(title: string, monogram: string, bold: MeasureFn, W: number = BILLBOARD_TEXTURE.width, H: number = BILLBOARD_TEXTURE.height): CreativeLayout {
  const band: Region = { x: 0, y: Math.round(H * 0.9), w: W, h: H - Math.round(H * 0.9) };
  const pad = Math.round(H * 0.1);
  const usableH = band.y - pad * 2;
  const tileSize = Math.round(Math.min(usableH, W * 0.3));
  const tile: Region = { x: pad, y: Math.round(pad + (usableH - tileSize) / 2), w: tileSize, h: tileSize };
  const monogramPx = fitFontSize(bold, monogram || " ", tile.w * 0.74, tile.h * 0.6, tile.h * 0.6, 12);
  const gap = Math.round(pad * 0.9);
  const titleBox: Region = { x: tile.x + tile.w + gap, y: pad, w: W - pad - (tile.x + tile.w + gap), h: usableH };
  const block = fitTextBlock(bold, title, titleBox, { maxPx: Math.round(H * 0.3), minPx: CREATIVE_MIN_TITLE_PX, maxLines: 2 });
  return { pad, tile, monogramPx, titleBox, title: block, band };
}

/** Contain-fit an image of (iw, ih) centred in `box` (never distorted; may upscale to fill the board). */
export function containRect(iw: number, ih: number, box: Region): Region {
  if (!(iw > 0) || !(ih > 0)) return { ...box };
  const s = Math.min(box.w / iw, box.h / ih);
  const w = iw * s;
  const h = ih * s;
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h };
}
